import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import EtatLieuxForm from '../components/EtatLieuxForm';
import {
  ClipboardList, Plus, ArrowLeft, Save, Loader2, FileText,
  MapPin, User, Calendar, Camera, CheckCircle2, AlertTriangle,
  ChevronRight, Trash2, Zap, Package, PaintBucket, DoorOpen,
  Blinds, Hammer, RectangleHorizontal, X, Eye, Upload,
  Building, Plug, Droplets, Layers, Fence, Ruler, Image, ShieldCheck, Pencil,
} from 'lucide-react';

const TYPES_BATIMENT = [
  { id: 'appartement', label: 'Appartement' },
  { id: 'maison', label: 'Maison' },
  { id: 'bureau', label: 'Bureau / Local' },
  { id: 'commerce', label: 'Commerce' },
  { id: 'ecole', label: 'École' },
  { id: 'mairie', label: 'Mairie' },
  { id: 'gymnase', label: 'Gymnase' },
  { id: 'salle_fetes', label: 'Salle des fêtes' },
  { id: 'creche', label: 'Crèche' },
  { id: 'logement_social', label: 'Logement social' },
  { id: 'ehpad', label: 'EHPAD' },
  { id: 'parking', label: 'Parking' },
  { id: 'autre', label: 'Autre' },
];

const TYPES_TRAVAUX = [
  { id: 'peinture', label: 'Peinture' },
  { id: 'parquet', label: 'Parquet' },
  { id: 'carrelage', label: 'Carrelage' },
  { id: 'porte', label: 'Portes' },
  { id: 'volet', label: 'Volets' },
  { id: 'electricite', label: 'Électricité' },
  { id: 'plomberie', label: 'Plomberie' },
  { id: 'faux_plafond', label: 'Faux plafond' },
  { id: 'cloison', label: 'Cloisons / Placo' },
  { id: 'facade', label: 'Ravalement' },
  { id: 'etancheite', label: 'Étanchéité' },
  { id: 'menuiserie_ext', label: 'Fenêtres' },
  { id: 'demolition', label: 'Démolition' },
];

const ETATS = [
  { id: 'neuf', label: 'Neuf' },
  { id: 'bon', label: 'Bon état' },
  { id: 'moyen', label: 'Moyen (préparation légère)' },
  { id: 'mauvais', label: 'Mauvais (gros travail)' },
];

const SIGNATAIRES = [
  { id: 'Anes', label: 'Anes' },
  { id: 'Issa', label: 'Issa' },
  { id: 'Nassim', label: 'Nassim' },
  { id: 'Mohammad-Ali', label: 'Mohammad-Ali' },
];

const CONTRAINTES = [
  { id: 'acces_difficile', label: 'Accès difficile' },
  { id: 'horaires', label: 'Horaires spécifiques' },
  { id: 'site_occupe', label: 'Site occupé' },
  { id: 'site_non_occupe', label: 'Site non occupé' },
  { id: 'urgence', label: 'Urgence' },
  { id: 'travaux_nuit', label: 'Travaux de nuit' },
  { id: 'permis_voirie', label: 'Permis voirie nécessaire' },
];

const STATUT_COLORS = {
  nouvelle: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Nouvelle' },
  incomplete: { bg: 'bg-red-100', text: 'text-red-700', label: 'Incomplète' },
  en_cours: { bg: 'bg-amber-100', text: 'text-amber-700', label: 'En cours' },
  devis_genere: { bg: 'bg-green-100', text: 'text-green-700', label: 'Devis généré' },
};

// Fields shown per work type
const FIELDS_PER_TYPE = {
  peinture: ['surface_murs', 'surface_plafond'],
  parquet: ['surface_sol', 'longueur_plinthes'],
  carrelage: ['surface_sol', 'longueur_plinthes'],
  porte: ['nombre_portes'],
  volet: ['nombre_volets'],
  electricite: ['nombre_points_elec', 'nombre_prises', 'nombre_interrupteurs', 'ml_cables', 'nb_tableaux'],
  plomberie: ['nombre_sanitaires', 'nombre_wc', 'nombre_lavabos', 'ml_plomberie', 'ml_evacuation'],
  faux_plafond: ['surface_plafond'],
  cloison: ['surface_cloisons'],
  facade: ['surface_facade'],
  etancheite: ['surface_etancheite', 'ml_releves'],
  menuiserie_ext: ['nombre_fenetres'],
  demolition: ['surface_demolition', 'volume_gravats'],
};

const FIELD_LABELS = {
  surface_sol: { label: 'Surface sol', unite: 'm²' },
  surface_murs: { label: 'Surface murs', unite: 'm²' },
  surface_plafond: { label: 'Surface plafond', unite: 'm²' },
  longueur_plinthes: { label: 'Plinthes', unite: 'ml' },
  surface_cloisons: { label: 'Surface cloisons', unite: 'm²' },
  surface_facade: { label: 'Surface façade', unite: 'm²' },
  surface_etancheite: { label: 'Surface étanchéité', unite: 'm²' },
  surface_demolition: { label: 'Surface démolition', unite: 'm²' },
  nombre_portes: { label: 'Nb portes', unite: '' },
  nombre_volets: { label: 'Nb volets', unite: '' },
  nombre_fenetres: { label: 'Nb fenêtres', unite: '' },
  nombre_points_elec: { label: 'Points lumineux', unite: '' },
  nombre_prises: { label: 'Prises', unite: '' },
  nombre_interrupteurs: { label: 'Interrupteurs', unite: '' },
  nombre_sanitaires: { label: 'Sanitaires', unite: '' },
  nombre_wc: { label: 'WC', unite: '' },
  nombre_lavabos: { label: 'Lavabos', unite: '' },
  ml_cables: { label: 'Câbles', unite: 'ml' },
  ml_plomberie: { label: 'Alim. eau', unite: 'ml' },
  ml_evacuation: { label: 'Évacuation', unite: 'ml' },
  ml_releves: { label: 'Relevés', unite: 'ml' },
  nb_tableaux: { label: 'Tableaux élec.', unite: '' },
  volume_gravats: { label: 'Gravats', unite: 'm³' },
};

export default function FichesVisite() {
  const [view, setView] = useState('list');
  const [fiches, setFiches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFiche, setSelectedFiche] = useState(null);
  const [generating, setGenerating] = useState(false);
  const fileInputRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [showTypeChooser, setShowTypeChooser] = useState(false);
  const [edlInitial, setEdlInitial] = useState(null);

  const [form, setForm] = useState({
    client_nom: '', adresse: '', contact_sur_place: '', telephone_contact: '', date_visite: '', heure_visite: '',
    type_batiment: '', nombre_pieces: '', etage: '', hauteur_sous_plafond: '',
    types_travaux: [], autre_travaux: '',
    pieces: [],
    surface_sol: '', surface_murs: '', surface_plafond: '', longueur_plinthes: '',
    surface_cloisons: '', surface_facade: '', surface_etancheite: '', surface_demolition: '',
    nombre_portes: '', nombre_volets: '', nombre_fenetres: '',
    nombre_points_elec: '', nombre_prises: '', nombre_interrupteurs: '',
    nombre_sanitaires: '', nombre_wc: '', nombre_lavabos: '',
    ml_cables: '', ml_plomberie: '', ml_evacuation: '', ml_releves: '',
    nb_tableaux: '', volume_gravats: '',
    etat_support: '', travaux_prevus: [], contraintes: [],
    autre_contrainte: '', photos_description: '', remarques: '',
    signataires: []
  });
  const [saving, setSaving] = useState(false);
  const [clients, setClients] = useState([]);
  const [photos, setPhotos] = useState([]); // local file previews
  const photosInputRef = useRef(null);
  const [clientsLoaded, setClientsLoaded] = useState(false);
  const [editingFicheId, setEditingFicheId] = useState(null);  // null = création, sinon = édition
  // Dimensions (longueur × largeur) for m² fields - auto-computes the surface
  const [dimensions, setDimensions] = useState({});
  // Photo lightbox in detail view
  const [lightboxUrl, setLightboxUrl] = useState(null);

  const resetForm = () => {
    setForm({
      client_nom: '', adresse: '', contact_sur_place: '', telephone_contact: '', date_visite: '', heure_visite: '',
      type_batiment: '', nombre_pieces: '', etage: '', hauteur_sous_plafond: '',
      types_travaux: [], autre_travaux: '', pieces: [],
      surface_sol: '', surface_murs: '', surface_plafond: '', longueur_plinthes: '',
      surface_cloisons: '', surface_facade: '', surface_etancheite: '', surface_demolition: '',
      nombre_portes: '', nombre_volets: '', nombre_fenetres: '',
      nombre_points_elec: '', nombre_prises: '', nombre_interrupteurs: '',
      nombre_sanitaires: '', nombre_wc: '', nombre_lavabos: '',
      ml_cables: '', ml_plomberie: '', ml_evacuation: '', ml_releves: '',
      nb_tableaux: '', volume_gravats: '',
      etat_support: '', travaux_prevus: [], contraintes: [],
      autre_contrainte: '', photos_description: '', remarques: '', signataires: [],
    });
    setPhotos([]);
    setDimensions({});
    setEditingFicheId(null);
  };

  const startEditFiche = (fiche) => {
    // Hydrate le formulaire avec la fiche existante
    const hydrated = {
      client_nom: fiche.client_nom || '',
      adresse: fiche.adresse || '',
      contact_sur_place: fiche.contact_sur_place || '',
      telephone_contact: fiche.telephone_contact || '',
      date_visite: fiche.date_visite || '',
      heure_visite: fiche.heure_visite || '',
      type_batiment: fiche.type_batiment || '',
      nombre_pieces: fiche.nombre_pieces?.toString() || '',
      etage: fiche.etage || '',
      hauteur_sous_plafond: fiche.hauteur_sous_plafond?.toString() || '',
      types_travaux: fiche.types_travaux || [],
      autre_travaux: fiche.autre_travaux || '',
      pieces: (fiche.pieces || []).map(p => ({
        ...p,
        photos: (p.photos || []).map(ph => ({ preview: ph.url, url: ph.url, existing: true, ...ph })),
        types_travaux: p.types_travaux || [],
        dimensions: p.dimensions || {},
        mesures: p.mesures || {},
      })),
      etat_support: fiche.etat_support || '',
      travaux_prevus: fiche.travaux_prevus || [],
      contraintes: fiche.contraintes || [],
      autre_contrainte: fiche.autre_contrainte || '',
      photos_description: fiche.photos_description || '',
      remarques: fiche.remarques || '',
      signataires: fiche.signataires || [],
    };
    // Champs numériques
    for (const k of Object.keys(FIELD_LABELS)) {
      hydrated[k] = fiche[k] != null ? String(fiche[k]) : '';
    }
    setForm(hydrated);
    setPhotos((fiche.photos || []).map(p => ({ preview: p.url, existing: true, ...p })));
    setEditingFicheId(fiche.fiche_id);
    setView('new');
  };

  // Update one dimension (longueur/largeur) for an m² field, recompute the surface
  const updateDim = (field, key, val) => {
    const d = { ...(dimensions[field] || { longueur: '', largeur: '' }), [key]: val };
    setDimensions(prev => ({ ...prev, [field]: d }));
    const L = parseFloat(d.longueur);
    const W = parseFloat(d.largeur);
    if (!isNaN(L) && !isNaN(W) && L > 0 && W > 0) {
      setForm(prev => ({ ...prev, [field]: (L * W).toFixed(2) }));
    } else {
      setForm(prev => ({ ...prev, [field]: '' }));
    }
  };

  // ============= MULTI-ROOM (pieces) helpers =============
  const addPiece = () => {
    const newPiece = {
      piece_id: `PCE-${Date.now()}-${Math.random().toString(36).substr(2, 4)}`,
      nom: '', longueur: '', largeur: '', hauteur_sous_plafond: '',
      types_travaux: [], travaux_prevus: '',
      etat_support: '',
      dimensions: {},  // per-surface field : { surface_murs: {longueur, largeur}, ... }
      mesures: {},     // per-numeric field : { nombre_portes: '3', ... }
      nombre_portes: '', nombre_volets: '', nombre_fenetres: '',
      nombre_points_elec: '', nombre_prises: '', nombre_interrupteurs: '',
      nombre_sanitaires: '', nombre_wc: '', nombre_lavabos: '',
      photos: [],
    };
    setForm(prev => ({ ...prev, pieces: [...(prev.pieces || []), newPiece] }));
  };

  const updatePiece = (idx, patch) => {
    setForm(prev => ({
      ...prev,
      pieces: prev.pieces.map((p, i) => i === idx ? { ...p, ...patch } : p)
    }));
  };

  const removePiece = (idx) => {
    setForm(prev => ({ ...prev, pieces: prev.pieces.filter((_, i) => i !== idx) }));
  };

  const togglePieceTravaux = (idx, type_id) => {
    const piece = form.pieces[idx];
    const newTypes = piece.types_travaux.includes(type_id)
      ? piece.types_travaux.filter(t => t !== type_id)
      : [...piece.types_travaux, type_id];
    updatePiece(idx, { types_travaux: newTypes });
  };

  const handlePiecePhotoSelect = (idx, e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map(f => ({
      file: f, preview: URL.createObjectURL(f), name: f.name
    }));
    updatePiece(idx, { photos: [...(form.pieces[idx].photos || []), ...newPhotos] });
  };

  const removePiecePhoto = (idx, photoIdx) => {
    const piece = form.pieces[idx];
    updatePiece(idx, { photos: piece.photos.filter((_, i) => i !== photoIdx) });
  };

  // -------- Métrés per-pièce (dérivés des corps de métier sélectionnés) --------
  const pieceRelevantFields = (piece) => {
    const set = new Set();
    (piece.types_travaux || []).forEach(t => {
      (FIELDS_PER_TYPE[t] || []).forEach(f => set.add(f));
    });
    return Array.from(set);
  };
  const updatePieceDim = (idx, field, key, val) => {
    const piece = form.pieces[idx];
    const dims = { ...(piece.dimensions || {}) };
    dims[field] = { ...(dims[field] || {}), [key]: val };
    // Auto-compute surface
    const L = parseFloat(dims[field].longueur) || 0;
    const W = parseFloat(dims[field].largeur) || 0;
    const patch = { dimensions: dims };
    if (L && W) patch[field] = (L * W).toFixed(2);
    updatePiece(idx, patch);
  };
  const updatePieceMesure = (idx, field, val) => {
    const piece = form.pieces[idx];
    const mes = { ...(piece.mesures || {}) };
    mes[field] = val;
    updatePiece(idx, { mesures: mes, [field]: val });
  };

  const pieceSurfaceM2 = (piece) => {
    const L = parseFloat(piece.longueur), W = parseFloat(piece.largeur);
    return (!isNaN(L) && !isNaN(W) && L > 0 && W > 0) ? L * W : 0;
  };

  useEffect(() => { loadFiches(); loadClients(); }, []);

  // Reload clients when entering form view
  useEffect(() => {
    if (view === 'new' && !clientsLoaded) {
      loadClients();
    }
  }, [view]);

  const loadFiches = async () => {
    setLoading(true);
    try {
      const res = await api.get('/fiches-visite');
      setFiches(res.data.fiches || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const loadClients = async () => {
    let allClients = [];
    try {
      const res1 = await api.get('/gestion-clients/category/collectivites');
      allClients = [...allClients, ...(res1.data.clients || [])];
    } catch (e) { console.error('Error loading collectivites:', e); }
    try {
      const res2 = await api.get('/gestion-clients/category/conducteurs');
      allClients = [...allClients, ...(res2.data.clients || [])];
    } catch (e) { console.error('Error loading conducteurs:', e); }
    setClients(allClients);
    setClientsLoaded(allClients.length > 0);
  };

  const handleSelectClient = (client) => {
    setForm({
      ...form,
      client_nom: client.nom
    });
  };

  const handlePhotosSelect = (e) => {
    const files = Array.from(e.target.files || []);
    const newPhotos = files.map(f => ({
      file: f,
      preview: URL.createObjectURL(f),
      name: f.name
    }));
    setPhotos(prev => [...prev, ...newPhotos]);
  };

  const removePhoto = (idx) => {
    setPhotos(prev => prev.filter((_, i) => i !== idx));
  };

  const toggleArray = (arr, val) => arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

  const handleSubmit = async () => {
    if (!form.client_nom || !form.adresse || form.signataires.length === 0) return;
    if (!form.pieces || form.pieces.length === 0) {
      alert('Vous devez ajouter au moins une pièce du chantier avant d\'enregistrer la fiche.');
      return;
    }
    // Vérifier qu'au moins une pièce a un nom
    const invalidPiece = form.pieces.find(p => !(p.nom || '').trim());
    if (invalidPiece) {
      alert('Chaque pièce doit avoir un nom.');
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form };
      for (const key of Object.keys(payload)) {
        if (FIELD_LABELS[key]) {
          // Convert numeric field: empty string -> null, otherwise parseFloat
          if (payload[key] === '' || payload[key] === null || payload[key] === undefined) {
            payload[key] = null;
          } else {
            payload[key] = parseFloat(payload[key]);
            if (isNaN(payload[key])) payload[key] = null;
          }
        }
      }
      if (payload.nombre_pieces) payload.nombre_pieces = parseInt(payload.nombre_pieces) || null;
      else payload.nombre_pieces = null;
      if (payload.hauteur_sous_plafond) payload.hauteur_sous_plafond = parseFloat(payload.hauteur_sous_plafond) || null;
      else payload.hauteur_sous_plafond = null;
      
      // Convert pieces numeric fields & strip local-only state (photos previews, _local_id)
      payload.pieces = (form.pieces || []).map(p => {
        const cleanPiece = {
          nom: p.nom || '',
          longueur: p.longueur === '' ? null : (parseFloat(p.longueur) || null),
          largeur: p.largeur === '' ? null : (parseFloat(p.largeur) || null),
          hauteur_sous_plafond: p.hauteur_sous_plafond === '' ? null : (parseFloat(p.hauteur_sous_plafond) || null),
          types_travaux: p.types_travaux || [],
          travaux_prevus: p.travaux_prevus || '',
          photos: [],
        };
        const L = cleanPiece.longueur, W = cleanPiece.largeur;
        if (L && W) cleanPiece.surface_m2 = Math.round(L * W * 100) / 100;
        ['nombre_portes', 'nombre_volets', 'nombre_fenetres', 'nombre_points_elec',
         'nombre_prises', 'nombre_interrupteurs', 'nombre_sanitaires', 'nombre_wc', 'nombre_lavabos'].forEach(f => {
          cleanPiece[f] = (p[f] === '' || p[f] == null) ? null : (parseInt(p[f]) || null);
        });
        return cleanPiece;
      });
      
      // Enrichit pièces avec état support / dimensions / mesures / piece_id existant
      payload.pieces = payload.pieces.map((cp, idx) => {
        const src = form.pieces[idx] || {};
        return {
          ...cp,
          piece_id: src.piece_id || undefined,
          etat_support: src.etat_support || '',
          dimensions: src.dimensions || {},
          mesures: src.mesures || {},
        };
      });

      let res;
      if (editingFicheId) {
        // Mode édition — utilise PUT
        res = await api.put(`/fiches-visite/${editingFicheId}`, payload);
        res.data = { fiche_id: editingFicheId, fiche: { ...payload, fiche_id: editingFicheId } };
      } else {
        res = await api.post('/fiches-visite', payload);
      }
      const ficheId = res.data.fiche_id;
      const createdPieces = res.data.fiche?.pieces || [];
      
      // Upload global photos (nouvelles uniquement)
      for (const photo of photos) {
        if (photo.existing || !photo.file) continue;
        const formData = new FormData();
        formData.append('file', photo.file);
        try {
          await api.post(`/fiches-visite/${ficheId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
        } catch (e) { console.error('Photo upload error:', e); }
      }
      
      // Upload per-piece photos (nouvelles uniquement)
      // En édition, recharge la fiche pour récupérer les piece_id actuels
      let piecesForUpload = createdPieces;
      if (editingFicheId) {
        try {
          const fresh = await api.get(`/fiches-visite/${ficheId}`);
          piecesForUpload = fresh.data?.fiche?.pieces || fresh.data?.pieces || [];
        } catch (e) {}
      }
      for (let pIdx = 0; pIdx < (form.pieces || []).length; pIdx++) {
        const localPiece = form.pieces[pIdx];
        const remotePiece = piecesForUpload[pIdx];
        if (!remotePiece || !localPiece.photos?.length) continue;
        for (const photo of localPiece.photos) {
          if (photo.existing || !photo.file) continue;
          const fd = new FormData();
          fd.append('file', photo.file);
          fd.append('piece_id', remotePiece.piece_id || '');
          try {
            await api.post(`/fiches-visite/${ficheId}/photos`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (e) { console.error('Piece photo upload error:', e); }
        }
      }
      
      setView('list');
      loadFiches();
      resetForm();
    } catch (e) { console.error(e); alert(e?.response?.data?.detail || 'Échec enregistrement'); }
    setSaving(false);
  };

  const handleGenererDevis = async (ficheId) => {
    setGenerating(true);
    try {
      const res = await api.post(`/fiches-visite/${ficheId}/generer-devis`);
      alert(res.data.message);
      loadFiches();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    setGenerating(false);
  };

  const handleDelete = async (ficheId) => {
    if (!window.confirm('Supprimer cette fiche ?')) return;
    try { await api.delete(`/fiches-visite/${ficheId}`); loadFiches(); if (selectedFiche?.fiche_id === ficheId) { setSelectedFiche(null); setView('list'); } } catch (e) {}
  };

  const handlePhotoUpload = async (ficheId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploadingPhoto(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      await api.post(`/fiches-visite/${ficheId}/photos`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      const res = await api.get(`/fiches-visite/${ficheId}`);
      setSelectedFiche(res.data);
    } catch (e) { console.error(e); }
    setUploadingPhoto(false);
  };

  // Get relevant fields based on selected work types
  const getRelevantFields = () => {
    const fields = new Set();
    form.types_travaux.forEach(type => {
      (FIELDS_PER_TYPE[type] || []).forEach(f => fields.add(f));
    });
    return Array.from(fields);
  };

  // ==========================================
  // ETAT DES LIEUX (avant démarrage chantier)
  // ==========================================
  if (view === 'etat_lieux') {
    return (
      <div className="p-6">
        <EtatLieuxForm
          initialFiche={edlInitial}
          onExit={() => { setEdlInitial(null); setView('list'); loadFiches(); }}
        />
      </div>
    );
  }

  // ==========================================
  // LIST VIEW
  // ==========================================
  if (view === 'list') {
    return (
      <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="fiches-visite-page">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Fiches Visite Terrain</h1>
            <p className="text-slate-500">Formulaires de visite → Devis automatiques</p>
          </div>
          <Button onClick={() => setShowTypeChooser(true)} className="bg-blue-600 hover:bg-blue-700" data-testid="new-fiche-btn">
            <Plus className="w-4 h-4 mr-2" /> Nouvelle fiche
          </Button>
        </div>

        {showTypeChooser && (
          <FicheTypeChooser
            onCancel={() => setShowTypeChooser(false)}
            onChoose={(t) => {
              setShowTypeChooser(false);
              if (t === 'devis') {
                setView('new');
              } else {
                setEdlInitial(null);
                setView('etat_lieux');
              }
            }}
          />
        )}

        {loading ? (
          <div className="text-center py-12"><Loader2 className="w-8 h-8 animate-spin text-slate-400 mx-auto" /></div>
        ) : fiches.length === 0 ? (
          <div className="text-center py-16 bg-white rounded-xl border border-slate-200">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <h3 className="font-semibold text-slate-900 mb-1">Aucune fiche de visite</h3>
            <p className="text-slate-500 text-sm mb-4">Créez une fiche après chaque visite terrain</p>
            <Button onClick={() => setShowTypeChooser(true)} className="bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-2" /> Nouvelle fiche</Button>
          </div>
        ) : (
          <div className="space-y-3">
            {fiches.map(f => {
              const st = STATUT_COLORS[f.statut] || STATUT_COLORS.nouvelle;
              return (
                <div key={f.fiche_id} className="bg-white rounded-xl p-4 border border-slate-200 hover:shadow-md transition-all cursor-pointer group"
                  onClick={() => {
                    if (f.type_fiche === 'etat_lieux') {
                      setEdlInitial(f); setView('etat_lieux');
                    } else {
                      setSelectedFiche(f); setView('detail');
                    }
                  }}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${f.type_fiche === 'etat_lieux' ? 'bg-emerald-100' : 'bg-blue-100'}`}>
                        {f.type_fiche === 'etat_lieux'
                          ? <ShieldCheck className="w-5 h-5 text-emerald-600" />
                          : <ClipboardList className="w-5 h-5 text-blue-600" />}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-slate-900">{f.client_nom}</p>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>{st.label}</span>
                          {f.type_fiche === 'etat_lieux' && (
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">État des lieux avant chantier</span>
                          )}
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-500 mt-0.5">
                          <span className="flex items-center gap-1"><MapPin className="w-3 h-3" /> {f.adresse}</span>
                          {f.date_visite && <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {f.date_visite}</span>}
                          {f.type_batiment && <span className="flex items-center gap-1"><Building className="w-3 h-3" /> {f.type_batiment}</span>}
                        </div>
                        <div className="flex gap-1 mt-1 flex-wrap">
                          {f.types_travaux?.map(t => (
                            <span key={t} className="px-2 py-0.5 bg-slate-100 rounded text-xs text-slate-600">{t}</span>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {f.statut === 'nouvelle' && f.type_fiche !== 'etat_lieux' && (
                        <Button size="sm" className="bg-green-600 hover:bg-green-700"
                          onClick={(e) => { e.stopPropagation(); handleGenererDevis(f.fiche_id); }} disabled={generating}>
                          {generating ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Zap className="w-3 h-3 mr-1" />} Générer devis
                        </Button>
                      )}
                      {f.devis_id && <span className="text-sm text-green-600 font-medium">{f.devis_id}</span>}
                      <button onClick={(e) => { e.stopPropagation(); handleDelete(f.fiche_id); }}
                        className="p-2 text-slate-300 hover:text-red-500 rounded-lg opacity-0 group-hover:opacity-100"><Trash2 className="w-4 h-4" /></button>
                      <ChevronRight className="w-4 h-4 text-slate-400" />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ==========================================
  // NEW FICHE FORM
  // ==========================================
  if (view === 'new') {
    const relevantFields = getRelevantFields();
    
    return (
      <div className="p-6 max-w-2xl mx-auto space-y-5" data-testid="fiche-form">
        <div className="flex items-center gap-3">
          <Button variant="ghost" onClick={() => { setView(editingFicheId ? 'detail' : 'list'); if (!editingFicheId) resetForm(); }} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
          <div>
            <h1 className="text-xl font-bold text-slate-900">{editingFicheId ? 'Modifier la fiche visite' : 'Fiche Visite Terrain'}</h1>
            {editingFicheId && <p className="text-xs text-slate-500">{editingFicheId} — modifications enregistrées à la validation</p>}
          </div>
        </div>

        {/* 1. Infos chantier */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">1</span>
            Infos chantier
          </h2>
          {/* Client selector */}
          {clients.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Sélectionner une collectivité existante</label>
              <div className="space-y-1.5 max-h-40 overflow-y-auto border border-slate-200 rounded-lg p-2">
                {clients.map((c, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => handleSelectClient(c)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-all ${
                      form.client_nom === c.nom
                        ? 'bg-blue-100 border border-blue-300 text-blue-800 font-medium'
                        : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                    }`}
                    data-testid={`select-client-${i}`}
                  >
                    <span className="font-medium">{c.nom}</span>
                    {c.adresse && <span className="text-xs text-slate-400 ml-2">({c.adresse})</span>}
                  </button>
                ))}
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Client / Collectivité *</label>
              <Input value={form.client_nom} onChange={e => setForm({...form, client_nom: e.target.value})} placeholder="Mairie de..." data-testid="fiche-client" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Contact sur place</label>
              <Input value={form.contact_sur_place} onChange={e => setForm({...form, contact_sur_place: e.target.value})} placeholder="Nom" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Adresse chantier *</label>
              <Input value={form.adresse} onChange={e => setForm({...form, adresse: e.target.value})} placeholder="Adresse complète" data-testid="fiche-adresse" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Téléphone contact</label>
              <Input value={form.telephone_contact} onChange={e => setForm({...form, telephone_contact: e.target.value})} placeholder="06..." /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Date visite</label>
              <Input type="date" value={form.date_visite} onChange={e => setForm({...form, date_visite: e.target.value})} /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Heure</label>
              <Input type="time" value={form.heure_visite} onChange={e => setForm({...form, heure_visite: e.target.value})} /></div>
          </div>
        </div>

        {/* 2. Type de bâtiment */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">2</span>
            Type de bâtiment
          </h2>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
            {TYPES_BATIMENT.map(t => (
              <button key={t.id} type="button" onClick={() => setForm({...form, type_batiment: t.id})}
                className={`p-2.5 rounded-lg border-2 text-xs font-medium transition-all ${form.type_batiment === t.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}>
                {t.label}
              </button>
            ))}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Nb pièces</label>
              <Input type="number" value={form.nombre_pieces} onChange={e => setForm({...form, nombre_pieces: e.target.value})} placeholder="0" /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Étage</label>
              <Input value={form.etage} onChange={e => setForm({...form, etage: e.target.value})} placeholder="RDC, 1er, 2e..." /></div>
            <div><label className="block text-xs font-medium text-slate-600 mb-1">Hauteur sous plafond</label>
              <Input type="number" step="0.1" value={form.hauteur_sous_plafond} onChange={e => setForm({...form, hauteur_sous_plafond: e.target.value})} placeholder="2.50" /></div>
          </div>
        </div>

        {/* 3. Pièces (multi-room) — chaque pièce a sa propre fiche */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3" data-testid="pieces-section">
          <div className="flex items-center justify-between">
            <h2 className="font-bold text-slate-900 flex items-center gap-2">
              <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">3</span>
              Pièces du chantier
              <span className="text-xs text-red-600 font-normal">(obligatoire — au moins 1 pièce)</span>
            </h2>
            <Button type="button" onClick={addPiece} size="sm" className="bg-blue-600 hover:bg-blue-700" data-testid="add-piece-btn">
              <Plus className="w-4 h-4 mr-1" /> Ajouter une pièce
            </Button>
          </div>

          {form.pieces.length === 0 ? (
            <div className="text-center py-6 text-sm text-red-500 border border-dashed border-red-200 bg-red-50/30 rounded-lg">
              <AlertTriangle className="w-5 h-5 mx-auto mb-2 text-red-500" />
              Au moins une pièce est requise. Cliquez sur "Ajouter une pièce" pour commencer.
            </div>
          ) : (
            <div className="space-y-3">
              {form.pieces.map((piece, pIdx) => {
                const surfM2 = pieceSurfaceM2(piece);
                return (
                  <div key={piece._local_id || pIdx} className="border-2 border-blue-100 rounded-xl p-4 bg-blue-50/30 space-y-3" data-testid={`piece-${pIdx}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex-1">
                        <label className="block text-xs font-semibold text-blue-700 mb-1">Pièce #{pIdx + 1}</label>
                        <Input
                          value={piece.nom}
                          onChange={e => updatePiece(pIdx, { nom: e.target.value })}
                          placeholder="Ex: Salon, Chambre 1, Salle de bain"
                          className="font-semibold"
                          data-testid={`piece-${pIdx}-nom`}
                        />
                      </div>
                      <Button type="button" variant="ghost" size="sm" onClick={() => removePiece(pIdx)} className="text-red-500 hover:bg-red-50 mt-5" data-testid={`piece-${pIdx}-remove`}>
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>

                    {/* Dimensions */}
                    <div className="bg-white rounded-lg p-3 border border-blue-100">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-700">Dimensions</span>
                        <span className="text-sm font-bold text-blue-700" data-testid={`piece-${pIdx}-surface`}>
                          = {surfM2.toFixed(2)} m²
                        </span>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Longueur (m)</label>
                          <Input type="number" step="0.01" inputMode="decimal" value={piece.longueur}
                            onChange={e => updatePiece(pIdx, { longueur: e.target.value })}
                            placeholder="0.00" data-testid={`piece-${pIdx}-longueur`} />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">Largeur (m)</label>
                          <Input type="number" step="0.01" inputMode="decimal" value={piece.largeur}
                            onChange={e => updatePiece(pIdx, { largeur: e.target.value })}
                            placeholder="0.00" data-testid={`piece-${pIdx}-largeur`} />
                        </div>
                        <div>
                          <label className="block text-xs text-slate-500 mb-1">H. sous plafond (m)</label>
                          <Input type="number" step="0.01" inputMode="decimal" value={piece.hauteur_sous_plafond}
                            onChange={e => updatePiece(pIdx, { hauteur_sous_plafond: e.target.value })}
                            placeholder="2.50" data-testid={`piece-${pIdx}-hsp`} />
                        </div>
                      </div>
                    </div>

                    {/* Corps de métier multi-select */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Corps de métier à intervenir</label>
                      <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                        {TYPES_TRAVAUX.map(t => {
                          const sel = piece.types_travaux.includes(t.id);
                          return (
                            <button key={t.id} type="button" onClick={() => togglePieceTravaux(pIdx, t.id)}
                              className={`px-2 py-2 rounded-lg border text-xs font-medium transition-all ${sel ? 'border-blue-500 bg-blue-100 text-blue-700' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'}`}
                              data-testid={`piece-${pIdx}-type-${t.id}`}>
                              {sel && <CheckCircle2 className="w-3 h-3 inline mr-0.5" />}{t.label}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    {/* Métrés dynamiques dérivés des corps de métier sélectionnés */}
                    {pieceRelevantFields(piece).length > 0 && (
                      <div className="bg-blue-50/40 border border-blue-100 rounded-lg p-3 space-y-3" data-testid={`piece-${pIdx}-metres`}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-blue-700">Mesures & quantités à relever</p>
                        {/* Surfaces (m²) : longueur × largeur */}
                        {pieceRelevantFields(piece).filter(f => FIELD_LABELS[f]?.unite === 'm²').map(field => {
                          const info = FIELD_LABELS[field];
                          const dim = (piece.dimensions || {})[field] || {};
                          const computed = parseFloat(piece[field]) || 0;
                          return (
                            <div key={field} className="bg-white border border-slate-200 rounded-lg p-2.5">
                              <div className="flex items-center justify-between mb-1.5">
                                <label className="text-xs font-semibold text-slate-700">{info.label}</label>
                                <span className="text-xs font-bold text-blue-700" data-testid={`piece-${pIdx}-${field}-result`}>= {computed.toFixed(2)} m²</span>
                              </div>
                              <div className="grid grid-cols-2 gap-2">
                                <Input type="number" step="0.01" inputMode="decimal" value={dim.longueur || ''}
                                  onChange={e => updatePieceDim(pIdx, field, 'longueur', e.target.value)}
                                  placeholder="Longueur (m)" data-testid={`piece-${pIdx}-${field}-l`} />
                                <Input type="number" step="0.01" inputMode="decimal" value={dim.largeur || ''}
                                  onChange={e => updatePieceDim(pIdx, field, 'largeur', e.target.value)}
                                  placeholder="Largeur (m)" data-testid={`piece-${pIdx}-${field}-w`} />
                              </div>
                            </div>
                          );
                        })}
                        {/* Non-m² (ml, unités, m³) */}
                        {pieceRelevantFields(piece).filter(f => FIELD_LABELS[f]?.unite !== 'm²').length > 0 && (
                          <div className="grid grid-cols-3 gap-2">
                            {pieceRelevantFields(piece).filter(f => FIELD_LABELS[f]?.unite !== 'm²').map(field => {
                              const info = FIELD_LABELS[field];
                              return (
                                <div key={field}>
                                  <label className="block text-xs font-medium text-slate-600 mb-1">{info.label} {info.unite && <span className="text-slate-400">({info.unite})</span>}</label>
                                  <Input type="number" inputMode="decimal" value={piece[field] || ''}
                                    onChange={e => updatePieceMesure(pIdx, field, e.target.value)}
                                    placeholder="0" data-testid={`piece-${pIdx}-${field}`} />
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}

                    {/* État du support (par pièce) */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">État du support</label>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                        {ETATS.map(e => (
                          <button key={e.id} type="button" onClick={() => updatePiece(pIdx, { etat_support: e.id })}
                            className={`p-2 rounded-lg border text-xs font-medium transition-all ${piece.etat_support === e.id ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                            data-testid={`piece-${pIdx}-etat-${e.id}`}>
                            {e.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Quantités optionnelles (portes/fenêtres/élec…) */}
                    <details className="bg-white rounded-lg border border-slate-200 group">
                      <summary className="cursor-pointer text-xs font-medium text-slate-600 p-2 hover:bg-slate-50">
                        Quantités spécifiques à cette pièce <span className="text-slate-400">(portes, fenêtres, prises, sanitaires…)</span>
                      </summary>
                      <div className="p-3 grid grid-cols-3 gap-2 border-t border-slate-100">
                        {[
                          { f: 'nombre_portes', l: 'Portes' },
                          { f: 'nombre_volets', l: 'Volets' },
                          { f: 'nombre_fenetres', l: 'Fenêtres' },
                          { f: 'nombre_points_elec', l: 'Points lumineux' },
                          { f: 'nombre_prises', l: 'Prises' },
                          { f: 'nombre_interrupteurs', l: 'Interrupteurs' },
                          { f: 'nombre_sanitaires', l: 'Sanitaires' },
                          { f: 'nombre_wc', l: 'WC' },
                          { f: 'nombre_lavabos', l: 'Lavabos' },
                        ].map(({ f, l }) => (
                          <div key={f}>
                            <label className="block text-xs text-slate-500 mb-1">{l}</label>
                            <Input type="number" inputMode="numeric" value={piece[f]}
                              onChange={e => updatePiece(pIdx, { [f]: e.target.value })}
                              placeholder="0" />
                          </div>
                        ))}
                      </div>
                    </details>

                    {/* Travaux prévus (texte libre) */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Travaux prévus / observations</label>
                      <Textarea value={piece.travaux_prevus}
                        onChange={e => updatePiece(pIdx, { travaux_prevus: e.target.value })}
                        placeholder="Ex: refaire l'enduit du plafond, remplacer la robinetterie, etc."
                        rows={2} data-testid={`piece-${pIdx}-travaux-prevus`} />
                    </div>

                    {/* Photos par pièce */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Photos de la pièce</label>
                      <div className="flex items-center gap-2 flex-wrap">
                        {(piece.photos || []).map((ph, phIdx) => (
                          <div key={phIdx} className="relative group">
                            <img src={ph.preview} alt="" className="w-20 h-20 object-cover rounded-lg border border-slate-200" />
                            <button type="button" onClick={() => removePiecePhoto(pIdx, phIdx)}
                              className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs opacity-0 group-hover:opacity-100">
                              <X className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                        <label className="w-20 h-20 rounded-lg border-2 border-dashed border-slate-300 flex items-center justify-center cursor-pointer hover:border-blue-400 hover:bg-blue-50">
                          <input type="file" accept="image/*" multiple className="hidden"
                            onChange={e => handlePiecePhotoSelect(pIdx, e)}
                            data-testid={`piece-${pIdx}-photos`} />
                          <Camera className="w-5 h-5 text-slate-400" />
                        </label>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div className="flex justify-center pt-2">
                <Button type="button" onClick={addPiece} variant="outline" size="sm" className="border-blue-300 text-blue-700">
                  <Plus className="w-4 h-4 mr-1" /> Ajouter une autre pièce
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* 4. Assistant technique IA (optionnel) */}
        <AIAssistantPanel
          fiche={selectedFiche}
          formClient={form.client_nom}
          formAdresse={form.adresse}
        />

        {/* 5. Contraintes */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">5</span>
            Contraintes chantier
          </h2>
          <div className="grid grid-cols-2 gap-2">
            {CONTRAINTES.map(c => {
              const sel = form.contraintes.includes(c.id);
              return (
                <button key={c.id} type="button" onClick={() => setForm({...form, contraintes: toggleArray(form.contraintes, c.id)})}
                  className={`p-2.5 rounded-lg border-2 text-sm font-medium transition-all text-left ${sel ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-slate-200 text-slate-600'}`}>
                  {sel ? <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" /> : null}{c.label}
                </button>
              );
            })}
          </div>
          <Input value={form.autre_contrainte} onChange={e => setForm({...form, autre_contrainte: e.target.value})} placeholder="Autre contrainte..." />
        </div>

        {/* 8. Photos */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">6</span>
            Photos <span className="text-xs text-slate-400 font-normal">(minimum 3 recommandées)</span>
          </h2>
          
          {/* Photo previews */}
          {photos.length > 0 && (
            <div className="grid grid-cols-4 gap-2">
              {photos.map((p, i) => (
                <div key={i} className="relative group">
                  <img src={p.preview} alt={p.name} className="w-full h-24 object-cover rounded-lg border border-slate-200" />
                  <button 
                    type="button"
                    onClick={() => removePhoto(i)}
                    className="absolute top-1 right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-3 h-3" />
                  </button>
                  <p className="text-xs text-slate-500 mt-0.5 truncate">{p.name}</p>
                </div>
              ))}
            </div>
          )}
          
          {/* Upload button */}
          <input
            type="file"
            ref={photosInputRef}
            onChange={handlePhotosSelect}
            accept="image/*"
            multiple
            capture="environment"
            className="hidden"
          />
          <button
            type="button"
            onClick={() => photosInputRef.current?.click()}
            className="w-full p-6 border-2 border-dashed border-blue-300 rounded-xl text-blue-600 hover:bg-blue-50 transition-colors flex flex-col items-center gap-2"
            data-testid="fiche-photos-upload"
          >
            <Camera className="w-8 h-8" />
            <span className="font-medium">Prendre une photo ou importer</span>
            <span className="text-xs text-slate-400">Vue générale · Détail zone · Contraintes</span>
          </button>
          
          {photos.length > 0 && (
            <p className="text-sm text-green-600 font-medium">{photos.length} photo(s) sélectionnée(s)</p>
          )}
        </div>

        {/* 9. Remarques */}
        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">7</span>
            Remarques terrain
          </h2>
          <Textarea value={form.remarques} onChange={e => setForm({...form, remarques: e.target.value})} rows={3} placeholder="Infos importantes, points d'attention, accès, parking..." />
        </div>

        {/* 10. Signataires */}
        <div className="bg-white rounded-xl border-2 border-blue-200 p-5 space-y-3">
          <h2 className="font-bold text-slate-900 flex items-center gap-2">
            <span className="w-6 h-6 bg-blue-600 text-white rounded-lg flex items-center justify-center text-xs">10</span>
            Signataire de la visite <span className="text-xs text-red-500 font-normal">(obligatoire · choix multiple)</span>
          </h2>
          <p className="text-xs text-slate-500">Personne(s) qui effectue(nt) la visite terrain</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {SIGNATAIRES.map(s => {
              const sel = form.signataires.includes(s.id);
              return (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setForm({...form, signataires: toggleArray(form.signataires, s.id)})}
                  data-testid={`fiche-signataire-${s.id}`}
                  className={`p-3 rounded-lg border-2 text-sm font-medium transition-all ${sel ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:border-slate-300'}`}
                >
                  {sel && <CheckCircle2 className="w-3.5 h-3.5 inline mr-1" />}{s.label}
                </button>
              );
            })}
          </div>
          {form.signataires.length === 0 && (
            <p className="text-xs text-red-500">Au moins un signataire doit être sélectionné</p>
          )}
        </div>

        {/* Submit */}
        <div className="flex gap-3 pb-8">
          <Button variant="outline" className="flex-1" onClick={() => { setView(editingFicheId ? 'detail' : 'list'); if (!editingFicheId) resetForm(); }}>Annuler</Button>
          <Button className="flex-1 bg-blue-600 hover:bg-blue-700" onClick={handleSubmit}
            disabled={saving || !form.client_nom || !form.adresse || form.signataires.length === 0} data-testid="fiche-submit">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
            {editingFicheId ? 'Enregistrer les modifications' : 'Enregistrer la fiche'}
          </Button>
        </div>
      </div>
    );
  }

  // ==========================================
  // DETAIL VIEW
  // ==========================================
  if (view === 'detail' && selectedFiche) {
    const f = selectedFiche;
    const st = STATUT_COLORS[f.statut] || STATUT_COLORS.nouvelle;
    return (
      <div className="p-6 max-w-3xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => { setView('list'); setSelectedFiche(null); }} className="p-2"><ArrowLeft className="w-5 h-5" /></Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900">{f.client_nom}</h1>
              <p className="text-sm text-slate-500">{f.fiche_id} · {f.adresse}</p>
            </div>
            <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${st.bg} ${st.text}`}>{st.label}</span>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => startEditFiche(f)} data-testid="edit-fiche-btn">
              <Pencil className="w-4 h-4 mr-2" /> Modifier
            </Button>
            {f.statut !== 'devis_genere' && (
              <Button className="bg-green-600 hover:bg-green-700" onClick={() => handleGenererDevis(f.fiche_id)} disabled={generating}>
                {generating ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Générer le devis
              </Button>
            )}
            {f.devis_id && (
              <Button variant="outline" onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/devis/${f.devis_id}/pdf`, '_blank')}>
                <FileText className="w-4 h-4 mr-2" /> PDF {f.devis_id}
              </Button>
            )}
          </div>
        </div>

        {f.statut === 'incomplete' && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-xl flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="font-medium text-red-700">Fiche incomplète — surfaces/quantités manquantes</p>
          </div>
        )}

        <div className="bg-white rounded-xl border border-slate-200 p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><p className="text-xs text-slate-400">Client</p><p className="font-medium">{f.client_nom}</p></div>
            <div><p className="text-xs text-slate-400">Adresse</p><p className="font-medium">{f.adresse}</p></div>
            <div><p className="text-xs text-slate-400">Contact</p><p className="font-medium">{f.contact_sur_place || '-'} {f.telephone_contact ? `(${f.telephone_contact})` : ''}</p></div>
            <div><p className="text-xs text-slate-400">Date / Bâtiment</p><p className="font-medium">{f.date_visite || '-'}{f.heure_visite ? ` à ${f.heure_visite}` : ''} · {f.type_batiment || '-'}</p></div>
          </div>
          {f.signataires?.length > 0 && (
            <div><p className="text-xs text-slate-400 mb-1">Signataire(s) de la visite</p>
              <div className="flex gap-1 flex-wrap">{f.signataires.map(s => <span key={s} className="px-2 py-1 bg-blue-600 text-white rounded text-sm font-medium">{s}</span>)}</div>
            </div>
          )}
          {f.types_travaux?.length > 0 && (
            <div><p className="text-xs text-slate-400 mb-1">Travaux</p>
              <div className="flex gap-1 flex-wrap">{f.types_travaux.map(t => <span key={t} className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm font-medium">{t}</span>)}</div>
            </div>
          )}
          <div className="grid grid-cols-3 gap-2">
            {Object.entries(FIELD_LABELS).map(([key, info]) => {
              const val = f[key];
              if (!val) return null;
              return (
                <div key={key} className="p-2 bg-slate-50 rounded-lg text-center">
                  <p className="text-xs text-slate-400">{info.label}</p>
                  <p className="font-bold text-slate-900">{val} {info.unite}</p>
                </div>
              );
            })}
          </div>
          {f.etat_support && <div><p className="text-xs text-slate-400">État support</p><p className="font-medium capitalize">{f.etat_support}</p></div>}
          {f.contraintes?.length > 0 && <div><p className="text-xs text-slate-400">Contraintes</p>
            <div className="flex gap-1 flex-wrap mt-1">{f.contraintes.map(c => <span key={c} className="px-2 py-1 bg-amber-100 text-amber-700 rounded text-xs">{c}</span>)}</div>
          </div>}
          {f.remarques && <div><p className="text-xs text-slate-400">Remarques</p><p className="text-sm text-slate-700 italic">"{f.remarques}"</p></div>}
          
          {/* Photos section */}
          {f.photos?.length > 0 && (
            <div><p className="text-xs text-slate-400 mb-2">Photos ({f.photos.length})</p>
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {f.photos.map((p, i) => {
                  const src = typeof p.url === 'string' ? `${process.env.REACT_APP_BACKEND_URL}${p.url}` : '';
                  return (
                    <button
                      key={i}
                      type="button"
                      onClick={() => src && setLightboxUrl(src)}
                      className="relative group rounded-lg overflow-hidden border border-slate-200 hover:border-blue-400 transition"
                      data-testid={`fiche-photo-${i}`}
                    >
                      {src ? (
                        <img src={src} alt={p.filename || `photo-${i}`} className="w-full h-28 object-cover" loading="lazy" />
                      ) : (
                        <div className="w-full h-28 bg-slate-100 flex items-center justify-center text-xs text-slate-400">
                          {p.filename || 'photo'}
                        </div>
                      )}
                      <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition flex items-center justify-center">
                        <Eye className="w-5 h-5 text-white opacity-0 group-hover:opacity-100" />
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Upload photo in detail view */}
          <div className="pt-2 border-t">
            <input type="file" ref={fileInputRef} onChange={(e) => handlePhotoUpload(f.fiche_id, e)} accept="image/*" className="hidden" />
            <Button variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploadingPhoto}>
              {uploadingPhoto ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Camera className="w-3 h-3 mr-1" />}
              Ajouter une photo
            </Button>
          </div>
        </div>

        {/* Photo Lightbox */}
        {lightboxUrl && (
          <div
            className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-4 cursor-zoom-out"
            onClick={() => setLightboxUrl(null)}
            data-testid="fiche-photo-lightbox"
          >
            <img src={lightboxUrl} alt="Photo agrandie" className="max-h-[92vh] max-w-[92vw] rounded-xl shadow-2xl" />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightboxUrl(null); }}
              className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center text-white"
              aria-label="Fermer"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        )}
      </div>
    );
  }

  return null;
}


/**
 * AI Assistant technique — chat contextuel qui aide le visiteur à ne rien oublier
 * lors d'une visite (remplacement matériel, questions techniques spécifiques).
 * Optionnel : n'apparaît que si le technicien active la conversation.
 */
function AIAssistantPanel({ fiche, formClient, formAdresse }) {
  const [expanded, setExpanded] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [contextNote, setContextNote] = useState('');
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages]);

  const canUse = !!fiche?.fiche_id;

  const send = async () => {
    if (!input.trim() || sending || !canUse) return;
    const newMessages = [...messages, { role: 'user', content: input.trim() }];
    setMessages(newMessages);
    setInput('');
    setSending(true);
    try {
      const r = await api.post(`/fiches-visite/${fiche.fiche_id}/assistant-technique`, {
        messages: newMessages,
        context: contextNote,
      });
      setMessages([...newMessages, { role: 'assistant', content: r.data.reply }]);
    } catch (e) {
      setMessages([...newMessages, { role: 'assistant', content: '⚠️ L\'assistant IA n\'a pas pu répondre. Réessayez dans un instant.' }]);
    }
    setSending(false);
  };

  return (
    <div className="bg-gradient-to-br from-violet-50 to-indigo-50 rounded-xl border border-violet-200 p-5 space-y-3" data-testid="ai-assistant-panel">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-slate-900 flex items-center gap-2">
          <span className="w-6 h-6 bg-violet-600 text-white rounded-lg flex items-center justify-center text-xs">4</span>
          Assistant technique IA
          <span className="text-xs text-slate-400 font-normal">(optionnel — pour les demandes techniques complexes)</span>
        </h2>
        <button type="button" onClick={() => setExpanded(!expanded)}
          className="text-xs text-violet-700 hover:text-violet-900 font-medium"
          data-testid="ai-toggle-btn">
          {expanded ? 'Masquer' : (canUse ? 'Ouvrir le chat' : 'Enregistrez d\'abord la fiche')}
        </button>
      </div>
      {expanded && (
        canUse ? (
          <div className="space-y-3">
            <p className="text-xs text-slate-600">
              Décris ce que tu vois ou ce qui est à faire (remplacement d'un chauffe-eau, changement d'une VMC,
              ajout d'un tableau électrique…). L'IA te posera toutes les questions à ne pas oublier pour éviter une seconde visite.
            </p>
            {!contextNote && messages.length === 0 && (
              <Input
                value={contextNote}
                onChange={e => setContextNote(e.target.value)}
                placeholder="Note de contexte (optionnel) : type d'intervention, pièce concernée…"
                data-testid="ai-context-input"
              />
            )}
            <div ref={listRef} className="bg-white rounded-lg border border-violet-200 p-3 space-y-3 max-h-96 overflow-y-auto">
              {messages.length === 0 ? (
                <p className="text-xs text-slate-400 italic text-center py-4">
                  Ex : « Le client veut remplacer son ballon d'eau chaude qui fuit. »
                </p>
              ) : messages.map((m, i) => (
                <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap ${
                    m.role === 'user' ? 'bg-violet-600 text-white' : 'bg-slate-100 text-slate-900'
                  }`} data-testid={`ai-msg-${i}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {sending && (
                <div className="flex justify-start">
                  <div className="bg-slate-100 text-slate-500 rounded-lg px-3 py-2 text-sm flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> L'assistant réfléchit…
                  </div>
                </div>
              )}
            </div>
            <div className="flex gap-2">
              <Textarea
                rows={2}
                value={input}
                onChange={e => setInput(e.target.value)}
                placeholder="Décris ta situation… (Shift+Entrée pour retour ligne)"
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
                className="flex-1"
                data-testid="ai-input"
              />
              <Button type="button" onClick={send} disabled={sending || !input.trim()}
                className="bg-violet-600 hover:bg-violet-700 text-white" data-testid="ai-send-btn">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <ChevronRight className="w-4 h-4" />}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-xs text-slate-500 italic">
            Enregistre d'abord les informations de base (client, adresse, signataire) et une première pièce.
            L'assistant IA sera ensuite disponible pour t'aider sur les points techniques spécifiques.
          </p>
        )
      )}
    </div>
  );
}


function FicheTypeChooser({ onCancel, onChoose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" data-testid="fiche-type-chooser">
      <div className="bg-white rounded-2xl max-w-2xl w-full overflow-hidden">
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold text-slate-900">Type de fiche de visite</h3>
          <button onClick={onCancel}><X className="w-5 h-5 text-slate-400" /></button>
        </div>
        <div className="p-6 grid md:grid-cols-2 gap-4">
          <button
            onClick={() => onChoose('devis')}
            className="text-left border-2 border-slate-200 hover:border-blue-500 rounded-2xl p-5 transition-all group"
            data-testid="choose-fiche-devis"
          >
            <div className="w-12 h-12 rounded-xl bg-blue-100 group-hover:bg-blue-200 flex items-center justify-center mb-3">
              <ClipboardList className="w-6 h-6 text-blue-600" />
            </div>
            <h4 className="font-bold text-slate-900 mb-1">Fiche pour devis</h4>
            <p className="text-sm text-slate-500">
              Visite commerciale — relevé des surfaces, quantités et travaux à prévoir.
              Génération automatique du devis à la fin.
            </p>
          </button>
          <button
            onClick={() => onChoose('etat_lieux')}
            className="text-left border-2 border-slate-200 hover:border-emerald-500 rounded-2xl p-5 transition-all group"
            data-testid="choose-fiche-etat-lieux"
          >
            <div className="w-12 h-12 rounded-xl bg-emerald-100 group-hover:bg-emerald-200 flex items-center justify-center mb-3">
              <ShieldCheck className="w-6 h-6 text-emerald-600" />
            </div>
            <h4 className="font-bold text-slate-900 mb-1">État des lieux avant chantier</h4>
            <p className="text-sm text-slate-500">
              Constat contradictoire avant démarrage — photos horodatées & géolocalisées,
              signatures client + Reno Resolve avec tampon. Se couvre en cas de litige.
            </p>
          </button>
        </div>
      </div>
    </div>
  );
}
